import { faClock, faStar as star } from '@fortawesome/free-regular-svg-icons';
import { faStar as solidStar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import { memo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { userInstrumentsSlice } from '!core/redux';
import { TrlFlag, TrlPriceType } from '»components/atoms';
import { TrlDateFormatted, TrlTimeFormatted } from '»utils/date';
import { getPercentageDecorator } from '»utils/endDecorator';
import { getFundType } from '»utils/funds';
import { displayNominalValue } from '»utils/nominalValue';
import { getDecimalDigits, TrlNumberFormatted } from '»utils/number';

import { TrlVariation } from '../TrlVariation';
import {
  BottomContainerStyled,
  BoxContainerStyled,
  BoxInlineContentStyled,
  BoxScheduleStyled,
  BoxTimeStyled,
  ContainerLeft,
  FavoriteIcon,
  LabelHeaderStyled,
  LabelStyled,
  LabelValueStyled,
  LeftContainerStyled,
  LeftInfoWrapper,
  MainDetailsStyled,
  MainInfoContainerStyled,
  QuotationStyled,
} from './TrlStockExchangeMarketDesc.styles';

export const TrlStockExchangeMarketDesc = memo(
  ({
    instrument,
    descr,
    securityDescription,
    symbol,
    exchange,
    exchangeDescr,
    country,
    lastPx,
    lastDateTime,
    totalVolumeTraded,
    nominalValue,
    variationPctg,
    priceRepresentation,
    quantityRepresentation,
    securityType,
    askPx,
    bidPx,
    highPx,
    lowPx,
    isCard,
    isFavorite,
    localStartTime,
    localEndTime,
    countryDescr,
    currency,
    chartData,
    canTrade,
    roundPrice,
  }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const dispatch = useDispatch();

    const isFunds = !!getFundType({ securityType, exchange });

    const setFavInstrument = () => {
      dispatch(
        userInstrumentsSlice.actions.setFavInstrumentRequest({
          option: isFavorite ? 'D' : 'I',
          instrument,
          symbol,
          exchange,
          securityType,
        }),
      );
    };

    const decimalDigits = getDecimalDigits(roundPrice);
    const titleDescription = descr || securityDescription;

    const endDecorator = getPercentageDecorator(priceRepresentation);

    return (
      <BoxContainerStyled isCard={isCard}>
        <MainInfoContainerStyled id="mainInfoContainer" isCard={isCard}>
          <LeftContainerStyled>
            <LabelHeaderStyled title={descr}>
              {isCard && (
                <FavoriteIcon
                  disabled={false}
                  icon={isFavorite ? solidStar : star}
                  onClick={setFavInstrument}
                  style={{ gridArea: 'favoriteIcon' }}
                />
              )}
              {titleDescription}
            </LabelHeaderStyled>
            <BoxInlineContentStyled>
              <MainDetailsStyled>{symbol}</MainDetailsStyled>
              {country && <TrlFlag flag={country} small />}
              <MainDetailsStyled>{exchangeDescr}</MainDetailsStyled>
              <MainDetailsStyled noBorder={isCard || !priceRepresentation}>
                {currency}
              </MainDetailsStyled>
              {!isCard && priceRepresentation && (
                <MainDetailsStyled noBorder>
                  <TrlPriceType value={priceRepresentation} />
                </MainDetailsStyled>
              )}
            </BoxInlineContentStyled>
            <BottomContainerStyled>
              <ContainerLeft isCard={isCard}>
                <QuotationStyled isCard={isCard}>
                  <TrlNumberFormatted
                    value={lastPx}
                    fontFamily="Roboto"
                    endDecorator={endDecorator}
                    decimalDigits={decimalDigits}
                  />
                </QuotationStyled>
                <BoxTimeStyled isCard={isCard}>
                  {lastDateTime ? (
                    <>
                      <TrlDateFormatted date={lastDateTime} />
                      {' | '}
                      <TrlTimeFormatted
                        value={lastDateTime}
                        hour="numeric"
                        minute="numeric"
                        second="numeric"
                      />
                    </>
                  ) : (
                    '-'
                  )}
                </BoxTimeStyled>
                <BoxInlineContentStyled
                  id="variableValuesContainer"
                  hide={isFunds}
                >
                  <LeftInfoWrapper isCard={isCard}>
                    <Box>
                      <LabelStyled isCard={isCard}>
                        <FormattedMessage
                          id={
                            isCard
                              ? 'TRLT_UI-NOMINAL_VALUE_SHORT'
                              : 'TRLT_UI-NOMINAL_VALUE'
                          }
                        />
                      </LabelStyled>
                      <LabelValueStyled isCard={isCard}>
                        <TrlNumberFormatted
                          value={displayNominalValue(
                            quantityRepresentation,
                            priceRepresentation,
                            nominalValue,
                          )}
                          fontFamily="Roboto"
                          decimalDigits={decimalDigits}
                        />
                      </LabelValueStyled>
                    </Box>
                    {isCard && (
                      <>
                        <Box marginTop="16px">
                          <LabelStyled isCard={isCard}>
                            <FormattedMessage id="TRLT_UI-MINIMUM" />
                          </LabelStyled>
                          <LabelValueStyled isCard={isCard} small>
                            <TrlNumberFormatted
                              value={lowPx}
                              fontFamily="Roboto"
                              decimalDigits={decimalDigits}
                            />
                          </LabelValueStyled>
                        </Box>
                        <Box>
                          <LabelStyled isCard={isCard}>
                            <FormattedMessage id="TRLT_UI-MAXIMUM" />
                          </LabelStyled>
                          <LabelValueStyled isCard={isCard} small>
                            <TrlNumberFormatted
                              value={highPx}
                              fontFamily="Roboto"
                              decimalDigits={decimalDigits}
                            />
                          </LabelValueStyled>
                        </Box>
                      </>
                    )}
                  </LeftInfoWrapper>
                  <Box>
                    <Box>
                      <LabelStyled isCard={isCard}>
                        <FormattedMessage id="TRLT_UI-VOLUME" />
                      </LabelStyled>
                      <LabelValueStyled isCard={isCard}>
                        <TrlNumberFormatted
                          value={totalVolumeTraded}
                          fontFamily="Roboto"
                          decimalDigits={decimalDigits}
                        />
                      </LabelValueStyled>
                    </Box>
                    {isCard && (
                      <>
                        <Box marginTop="16px">
                          <LabelStyled isCard={isCard}>
                            <FormattedMessage id="TRLT_UI-PURCHASE" />
                          </LabelStyled>
                          <LabelValueStyled isCard={isCard} small>
                            <TrlNumberFormatted
                              value={bidPx}
                              fontFamily="Roboto"
                              decimalDigits={decimalDigits}
                            />
                          </LabelValueStyled>
                        </Box>
                        <Box>
                          <LabelStyled isCard={isCard}>
                            <FormattedMessage id="TRLT_UI-SALE" />
                          </LabelStyled>
                          <LabelValueStyled isCard={isCard} small>
                            <TrlNumberFormatted
                              value={askPx}
                              fontFamily="Roboto"
                              decimalDigits={decimalDigits}
                            />
                          </LabelValueStyled>
                        </Box>
                      </>
                    )}
                  </Box>
                </BoxInlineContentStyled>
              </ContainerLeft>
              {(isMobile || isCard) && (
                <TrlVariation
                  variationPctg={variationPctg}
                  chartData={chartData}
                  isCard={isCard}
                  canTrade={canTrade}
                  symbol={symbol}
                  exchange={exchange}
                  securityType={securityType}
                  decimalDigits={decimalDigits}
                />
              )}
            </BottomContainerStyled>
          </LeftContainerStyled>
          {!isMobile && !isCard && (
            <TrlVariation
              variationPctg={variationPctg}
              chartData={chartData}
              symbol={symbol}
              exchange={exchange}
              securityType={securityType}
              decimalDigits={decimalDigits}
            />
          )}
        </MainInfoContainerStyled>
        {!isCard && !isFunds && (
          <BoxScheduleStyled>
            <FontAwesomeIcon icon={faClock} style={{ marginRight: '7px' }} />
            <FormattedMessage
              id="TRLT_UI-SCHEDULE"
              values={{
                startTime: <TrlTimeFormatted value={localStartTime} />,
                endTime: <TrlTimeFormatted value={localEndTime} />,
                country: countryDescr,
              }}
            />
          </BoxScheduleStyled>
        )}
      </BoxContainerStyled>
    );
  },
);

TrlStockExchangeMarketDesc.displayName = 'TrlStockExchangeMarketDesc';

TrlStockExchangeMarketDesc.propTypes = {
  instrument: PropTypes.number.isRequired,
  descr: PropTypes.string,
  securityDescription: PropTypes.string,
  symbol: PropTypes.string.isRequired,
  exchange: PropTypes.string.isRequired,
  exchangeDescr: PropTypes.string.isRequired,
  country: PropTypes.string.isRequired,
  lastPx: PropTypes.number.isRequired,
  lastDateTime: PropTypes.string,
  totalVolumeTraded: PropTypes.number,
  nominalValue: PropTypes.number,
  variationPctg: PropTypes.number.isRequired,
  priceRepresentation: PropTypes.string,
  quantityRepresentation: PropTypes.string,
  securityType: PropTypes.string.isRequired,
  askPx: PropTypes.number,
  bidPx: PropTypes.number,
  highPx: PropTypes.number,
  lowPx: PropTypes.number,
  isCard: PropTypes.bool,
  isFavorite: PropTypes.bool,
  localStartTime: PropTypes.string,
  localEndTime: PropTypes.string,
  countryDescr: PropTypes.string,
  currency: PropTypes.string.isRequired,
  chartData: PropTypes.array,
  canTrade: PropTypes.string,
  roundPrice: PropTypes.number.isRequired,
};
